/** BCP-47 fallback when the browser doesn't surface a locale (rare but
 *  possible). WONE is a UK product — en-GB matches the rest of the app's
 *  British copy. */
export const FALLBACK_LANGUAGE_TAG = "en-GB";

/**
 * The preferred-language values Platform accepts (see the backend
 * `UserDetail.language` choices). `en-GB` is the canonical default every new
 * profile starts on, so it doubles as the "not yet chosen" signal client-side.
 */
export const SUPPORTED_LOCALES = ["en-GB", "fr-FR", "pt-PT", "es-ES"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = "en-GB";

/** The browser's primary locale as a BCP-47 tag (e.g. `fr-FR`, `en-US`). */
export const getBrowserLanguageTag = (): string =>
  navigator.language || FALLBACK_LANGUAGE_TAG;

/**
 * Map an arbitrary BCP-47 tag to the supported locale that shares its base
 * language, or null if we don't support that language. Region is ignored on
 * input (a French speaker on `fr-CA` seeds `fr-FR`) because we ship one
 * regional variant per language today.
 */
export const resolveSupportedLocale = (tag: string): SupportedLocale | null => {
  const base = tag.toLowerCase().split("-")[0];
  return (
    SUPPORTED_LOCALES.find((locale) => locale.toLowerCase().startsWith(base)) ??
    null
  );
};
